/**
 * Client-side bounce from a retired legacy family shell to the same
 * family label on the canonical root for that environment.
 *
 * The SPA does this (rather than a DNS/edge rule) because both estates
 * serve the same Static Web App: while the legacy CNAMEs still point
 * here, whichever host the customer bookmarked keeps working, and the
 * bundle moves them once — preserving path, query, and fragment so a
 * deep link into a vault document survives the move.
 *
 * Deliberately inert until `VITE_PORTAL_DOMAIN_ROOT` names a canonical
 * root. Code ships before DNS; a bundle built for the legacy estate
 * must never send customers to hosts that do not resolve yet.
 *
 * The legacy → canonical mapping is per environment
 * (`PORTAL_ROOT_MIGRATION_PAIRS`), so dev rehearses the same code path
 * and a dev bundle can never fling a production visitor at the dev
 * estate.
 *
 * Out of scope on purpose:
 *   - localhost (vite dev serves the SPA directly)
 *   - legacy leaf vanity hosts (`testra.portal.latitude30.co`) — only
 *     the three family shells exist on the canonical roots
 */

import {
  PORTAL_ROOT_MIGRATION_PAIRS,
  isPortalFamilyHostLabel,
  normalisePortalDomainRoot,
} from "@l30/shared";

const LABEL_PATTERN = /^[a-z0-9-]+$/;

interface LegacyRedirectInput {
  /** `window.location.host` — may carry a port. */
  host: string;
  pathname?: string;
  search?: string;
  hash?: string;
  /** `VITE_PORTAL_DOMAIN_ROOT` for this build. */
  configuredRoot?: string | undefined;
}

/**
 * Absolute URL to send the browser to, or `null` to stay put.
 */
export function legacyPortalRedirectUrl(input: LegacyRedirectInput): string | null {
  if (!input.configuredRoot) return null;
  const configuredRoot = normalisePortalDomainRoot(input.configuredRoot);
  const pair = PORTAL_ROOT_MIGRATION_PAIRS.find((p) => p.canonical === configuredRoot);
  if (!pair) return null;

  const hostname = input.host.split(":")[0]?.toLowerCase() ?? "";
  const suffix = `.${pair.legacy}`;
  if (!hostname.endsWith(suffix)) return null;

  const label = hostname.slice(0, -suffix.length);
  if (!LABEL_PATTERN.test(label)) return null;
  if (!isPortalFamilyHostLabel(label)) return null;

  const pathname = input.pathname && input.pathname.startsWith("/") ? input.pathname : "/";
  return `https://${label}.${pair.canonical}${pathname}${input.search ?? ""}${input.hash ?? ""}`;
}

/**
 * Bounce the current window when it sits on a retired family host.
 * Returns true when a navigation was issued, so the caller can skip
 * mounting the router.
 */
export function redirectLegacyPortalHost(): boolean {
  if (typeof window === "undefined") return false;
  const target = legacyPortalRedirectUrl({
    host: window.location.host,
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    configuredRoot: import.meta.env.VITE_PORTAL_DOMAIN_ROOT,
  });
  if (!target) return false;
  window.location.replace(target);
  return true;
}
